/**
 * PolicyModelsSection — the Models tab: the ordered model list (tried first to last), the fallback policy and the
 * thinking mode. Template example: NextAdmin `(dashboard)/(home)/_component/top-products` (ItemList in a Card) and
 * demo `/form-layouts`.
 */
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import type React from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { FormFullWidth, FormSection, SelectFormField } from '../../../components/form/index.js';
import { Badge, Button, Callout, FilterSelect, ItemList } from '../../../components/ui/index.js';
import type { ItemListItem } from '../../../components/ui/index.js';
import { modelBankQuery, runtimePoliciesQuery } from '../../../services/api/runtime-policy.queries.js';
import type { SectionProps } from './PolicyEditorSectionsA.js';
import { RUNTIME_POLICY_FIELD_HELP } from '../../../help/runtime-policy-fields.js';
import { NO_FALLBACK, THINKING_MODES, enumOptions } from './types.js';

interface PolicyModelsSectionProps extends SectionProps {
  /** The policy being edited; null while creating. Excluded from its own fallback choices. */
  policyId: number | null;
}

function move<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item === undefined) return next;
  next.splice(to, 0, item);
  return next;
}

export const PolicyModelsSection: React.FC<PolicyModelsSectionProps> = ({ canEdit, policyId }) => {
  const { field } = useController({ name: 'modelIds' });
  const modelIds: number[] = Array.isArray(field.value) ? field.value : [];
  const bank = useQuery(modelBankQuery());
  const policies = useQuery(runtimePoliciesQuery());
  const models = bank.data ?? [];
  const byId = new Map(models.map((m) => [m.id, m]));
  const available = models.filter((m) => !modelIds.includes(m.id));
  const chosenFallback = useWatch({ name: 'fallback' });
  const fallbackOptions = [
    { value: NO_FALLBACK, label: 'No fallback' },
    // The policy already chosen stays listed even if it is no longer active, so the select never shows blank.
    ...(policies.data ?? [])
      .filter((p) => p.id !== policyId && (p.status === 'active' || String(p.id) === chosenFallback))
      .map((p) => ({ value: String(p.id), label: p.name })),
  ];

  const items: ItemListItem[] = modelIds.map((id, index) => {
    const model = byId.get(id);
    return {
      id: String(id),
      leading: <span className="w-5 text-sm text-faint">{index + 1}</span>,
      title: model?.displayName ?? `Model ${id}`,
      subtitle: model && (
        <span className="flex items-center gap-2">
          {model.provider}
          {!model.enabled && <Badge variant="warning">Disabled</Badge>}
        </span>
      ),
      value: canEdit && (
        <span className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Move ${model?.displayName ?? `model ${id}`} up`}
            disabled={index === 0}
            onClick={() => field.onChange(move(modelIds, index, index - 1))}
          >
            <ArrowUp size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Move ${model?.displayName ?? `model ${id}`} down`}
            disabled={index === modelIds.length - 1}
            onClick={() => field.onChange(move(modelIds, index, index + 1))}
          >
            <ArrowDown size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Remove ${model?.displayName ?? `model ${id}`}`}
            onClick={() => field.onChange(modelIds.filter((m) => m !== id))}
          >
            <X size={14} aria-hidden="true" />
          </Button>
        </span>
      ),
    };
  });

  return (
    <>
      <FormSection title="Models" description="Tried first to last. The next model runs when one fails.">
        <FormFullWidth>
          <div className="space-y-4">
            {bank.isError && (
              <Callout tone="danger">Could not load the model bank: models show by number only.</Callout>
            )}
            {policies.isError && (
              <Callout tone="danger">Could not load the policies: no fallback can be chosen now.</Callout>
            )}
            {modelIds.length === 0 ? (
              <p className="text-sm text-muted">No models yet. Add at least one.</p>
            ) : (
              <ItemList items={items} aria-label="Models in order" />
            )}
            {canEdit && (
              <FilterSelect
                label="Add model"
                value=""
                placeholder={available.length > 0 ? 'Add a model…' : 'All models added'}
                options={available.map((m) => ({ value: String(m.id), label: `${m.displayName} (${m.provider})` }))}
                onChange={(value) => {
                  if (value !== '') field.onChange([...modelIds, Number(value)]);
                }}
                className="w-72"
              />
            )}
          </div>
        </FormFullWidth>
      </FormSection>
      <FormSection title="Fallback and thinking" description="What runs when every model above fails.">
        <SelectFormField
          name="fallback"
          help={RUNTIME_POLICY_FIELD_HELP.fallback}
          label="Fallback policy"
          disabled={!canEdit}
          options={fallbackOptions}
        />
        <SelectFormField
          name="thinking"
          help={RUNTIME_POLICY_FIELD_HELP.thinking}
          label="Thinking"
          disabled={!canEdit}
          options={enumOptions(THINKING_MODES)}
        />
      </FormSection>
    </>
  );
};
